import React, { useCallback, useMemo, useState } from 'react'
import styles from './MultiSelect.module.css'
import cx from 'classnames'
import Checkbox from '../Checkbox'
import Loader from '../Loader'

export type SelectOption = {
    value: string
    label: string
}

type SelectProps = {
    options: SelectOption[]
    onChange: (property: string, value: string, single: SelectOption) => void
    property: string
    selected: string[]
    placeholder: string
    className: string | undefined
    loading?: boolean
    searchable?: boolean
}

const MultiSelect: React.FC<SelectProps> = ({
    options,
    onChange,
    property,
    placeholder,
    className,
    loading,
    selected,
    searchable = false,
}) => {
    const [isDropdownOpen, setDropdownOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')

    const normalizedSelected = useMemo(
        () => selected.map((value) => String(value)),
        [selected]
    )

    const filteredOptions = useMemo(() => {
        if (!searchable || searchTerm.trim() === '') {
            return options
        }

        const term = searchTerm.toLowerCase()
        return options.filter((option) =>
            option.label.toLowerCase().includes(term)
        )
    }, [options, searchable, searchTerm])

    const handleCheck = useCallback(
        (option: SelectOption) => {
            const optionValue = String(option.value)
            const data = normalizedSelected.includes(optionValue)
                ? normalizedSelected.filter((value) => value !== optionValue)
                : [...normalizedSelected, optionValue]

            onChange(property, data.join(','), option)
        },
        [normalizedSelected, onChange, property]
    )

    const displayValue = isDropdownOpen
        ? searchTerm
        : normalizedSelected.length === 0
          ? ''
          : `${normalizedSelected.length} ausgewählt`

    return (
        <div className={cx(styles['zr-select-wrapper'], className)}>
            <input
                type="text"
                className={cx(
                    styles['zr-search-input'],
                    isDropdownOpen && styles['zr-search-active']
                )}
                value={displayValue}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={placeholder}
                onFocus={() => setDropdownOpen(true)}
                onBlur={() => setTimeout(() => setDropdownOpen(false), 200)}
                disabled={!searchable}
            />
            {!searchable && (
                <button
                    type="button"
                    className={cx(styles['zr-cover-search'])}
                    onClick={() => setDropdownOpen((prev) => !prev)}
                />
            )}
            <label
                className={cx(normalizedSelected.length > 0 && styles['zr-filled'])}
            >
                {placeholder}
            </label>
            <span className={cx(styles['focus-border'])}>
                <i />
            </span>
            {isDropdownOpen && filteredOptions.length > 0 && (
                <div className={cx(styles['zr-dropdown'])}>
                    {filteredOptions.map((option) => (
                        <Checkbox
                            key={option.value}
                            label={option.label}
                            property={String(option.value)}
                            value={normalizedSelected.includes(
                                String(option.value)
                            )}
                            onChange={() => handleCheck(option)}
                            classes={cx(styles['zr-dropdown-item'])}
                        />
                    ))}
                    {loading && (
                        <div
                            key={'loading'}
                            className={cx(styles['zr-dropdown-item'])}
                        >
                            <Loader isLoading={loading} />
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default MultiSelect
